// src/pages/Gallery.jsx
import React, { useEffect, useState } from "react";
import API, { uploadUrl } from "../utils/api";
import { X, Search, Sparkles } from "lucide-react";

export default function Gallery() {
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  const getImageSrc = (image) => {
    if (!image) return "";
    if (image.startsWith("http")) return image;
    if (image.startsWith("uploads/")) {
      return `${uploadUrl}/${image.replace("uploads/", "")}`;
    }
    return `${uploadUrl}/${image}`;
  };

  const fetchGallery = async () => {
    try {
      const response = await API.get("/gallery");

      const galleryList = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];

      setGalleryImages(galleryList);
    } catch (error) {
      console.log("Gallery API Error:", error);
      setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  return (
    <main className="w-full bg-white overflow-hidden font-[Inter]">
      <section className="w-full bg-gradient-to-br from-white via-[#FFF9F0] to-[#EAF7ED] py-16 lg:py-20 px-4 sm:px-6 lg:px-16 xl:px-24">
        <div className="max-w-[1400px] mx-auto text-center">
          <span className="inline-flex items-center gap-2 bg-[#FFF3E6] text-[#F28C1B] px-5 py-2 rounded-full text-xs font-bold shadow-sm">
            <Sparkles size={15} />
            GALLERY
          </span>

          <h1 className="mt-5 text-2xl md:text-4xl font-extrabold text-[#07135C]">
            Explore Our Work, Research & Cultivation
          </h1>

          <p className="mt-4 max-w-3xl mx-auto text-gray-600 text-sm md:text-[15px] leading-7 font-medium">
            A visual glimpse into Urban Agrotech Farm’s mushroom cultivation,
            laboratory practices, training programs and wellness-focused
            nutraceutical journey.
          </p>
        </div>
      </section>

      <section className="w-full py-16 lg:py-20 px-4 sm:px-6 lg:px-16 xl:px-24 bg-white">
        <div className="max-w-[1400px] mx-auto">
          {loading ? (
            <p className="text-center text-[#07135C] font-bold">
              Loading gallery...
            </p>
          ) : galleryImages.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((item) => (
                <button
                  key={item._id}
                  type="button"
                  onClick={() => setSelectedImage(item)}
                  className="group relative h-[320px] rounded-3xl overflow-hidden shadow-md hover:shadow-2xl text-left transition-all duration-300"
                >
                  <img
                    src={getImageSrc(item.image)}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-[#07135C]/90 via-[#07135C]/20 to-transparent opacity-90"></div>

                  <div className="absolute top-5 right-5 w-11 h-11 bg-white text-[#F28C1B] rounded-full flex items-center justify-center opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition duration-300">
                    <Search size={20} />
                  </div>

                  <div className="absolute left-5 bottom-5 right-5">
                    <span className="inline-block bg-[#F28C1B] text-white px-4 py-1.5 rounded-full text-xs font-bold mb-3">
                      {item.category || "Gallery"}
                    </span>

                    <h3 className="text-white text-lg md:text-xl font-extrabold">
                      {item.title}
                    </h3>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 font-semibold">
              No gallery images found.
            </p>
          )}
        </div>
      </section>

      {selectedImage && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center px-4">
          <button
            type="button"
            onClick={() => setSelectedImage(null)}
            className="absolute top-5 right-5 w-11 h-11 bg-white text-[#07135C] rounded-full flex items-center justify-center"
          >
            <X size={24} />
          </button>

          <div className="max-w-5xl w-full bg-white p-3 rounded-3xl">
            <img
              src={getImageSrc(selectedImage.image)}
              alt={selectedImage.title}
              className="w-full max-h-[75vh] object-contain rounded-2xl"
            />

            <div className="p-4">
              <p className="text-[#F28C1B] text-sm font-bold">
                {selectedImage.category || "Gallery"}
              </p>

              <h3 className="text-[#07135C] text-xl md:text-2xl font-extrabold mt-1">
                {selectedImage.title}
              </h3>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
